/**
 * Client-Side Attribution Helper
 *
 * Captures marketing attribution data from URL parameters and browser context.
 * Used for HubSpot lead tracking and funnel analytics.
 */

export interface AttributionData {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  landingPage: string | null;
  referrerUrl: string | null;
}

/**
 * Captures attribution data from URL and browser context.
 *
 * @returns AttributionData object with captured values
 */
export function captureAttribution(): AttributionData {
  try {
    // Parse URL parameters
    const searchParams = new URLSearchParams(window.location.search);

    // Capture UTM parameters (case-insensitive)
    const utmSource = searchParams.get('utm_source') || searchParams.get('UTM_SOURCE');
    const utmMedium = searchParams.get('utm_medium') || searchParams.get('UTM_MEDIUM');
    const utmCampaign = searchParams.get('utm_campaign') || searchParams.get('UTM_CAMPAIGN');

    // Capture landing page (pathname only, no query string)
    const landingPage = window.location.pathname;

    // Capture referrer (external sites only, not internal navigation)
    const referrer = document.referrer;
    const referrerUrl = referrer && !referrer.includes(window.location.hostname)
      ? referrer
      : null;

    return {
      utmSource: utmSource || null,
      utmMedium: utmMedium || null,
      utmCampaign: utmCampaign || null,
      landingPage: landingPage || null,
      referrerUrl: referrerUrl,
    };
  } catch (error) {
    // Fail silently - attribution is best-effort
    console.warn('Failed to capture attribution data:', error);
    return {
      utmSource: null,
      utmMedium: null,
      utmCampaign: null,
      landingPage: null,
      referrerUrl: null,
    };
  }
}

/**
 * Gets HubSpot tracking cookie if available.
 */
export function getHubspotCookie(): string {
  try {
    return document.cookie.match(/hubspotutk=([^;]+)/)?.[1] || '';
  } catch {
    return '';
  }
}
